import React, {Component, Fragment} from "react";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {connect} from "react-redux";
import {Link} from "react-router-dom";

import {close, threeDots} from "../icons";
import Dropdown from "../common/Dropdown";
import Modal from "../common/modal/Modal";
import ModalHeader from "../common/modal/ModalHeader";
import ModalBody from "../common/modal/ModalBody";
import ModalFooter from "../common/modal/ModalFooter";
import {showNotification} from "../../../store/slices/admin/notification";
import { deleteDashboard, fetchDashboards, setFetchParams } from "../../../store/slices/admin/dashboards";
import {formatDate} from "../helper/formatters";
import {ITemplateConfig} from "../../../constants/interfaces";
import TableHeading from "./TableHeading";
import FetchPaginatedData from "../common/FetchPaginatedData";
import Pagination from "../common/Pagination";
import { DebouncedFunc, debounce } from "lodash";

type Props = {
    troubleLoading: boolean,
    isSuperUser: boolean,
    // injected
    dispatch: Function,
    sort: string,
    order: string,
    page: number,
    searchTerm: string
}

type State = {
    isCopyModalOpen: boolean,
    copyModalUrl: string,
    isDeleteModalOpen: boolean,
    deleteId: string | number,
    searchTerm: string, // We duplicate searchTerm in order to get debounce to work properly
}

class Table extends Component<Props, State> {
    sendSearchParams: DebouncedFunc<() => void>;

    constructor(props: Props) {
        super(props);
        
        this.state = {
            isCopyModalOpen: false,
            copyModalUrl: "",
            isDeleteModalOpen: false,
			deleteId: "",
            searchTerm: ''
        }
        
        this.toggleCopyModal = this.toggleCopyModal.bind(this);
        this.handleCopy = this.handleCopy.bind(this);
        this.toggleDeleteModal = this.toggleDeleteModal.bind(this);
        this.deleteDashboard = this.deleteDashboard.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.sendSearchParams = debounce(this._sendSearchParams.bind(this), 600);
        this.changePage = this.changePage.bind(this);
    }

    componentDidMount() {
        this.props.dispatch(setFetchParams({ sort: this.props.sort, order: this.props.order, page: 1, searchTerm: ''}, false));
    }

    getDashboardUrl(id: number | string) {
    	return `${window.location.origin}/dashboards/${id}`;
	}

	toggleCopyModal(id: number | string) {
        this.setState({
            isCopyModalOpen: !this.state.isCopyModalOpen,
            copyModalUrl: this.getDashboardUrl(id)
        });
    }
    
    handleCopy() {
        this.props.dispatch(showNotification('success', 'URL copied to clipboard!'));
    }
    
    toggleDeleteModal(id: number | string) {
        this.setState({
            isDeleteModalOpen: !this.state.isDeleteModalOpen,
			deleteId: id
        });
    }
    
    deleteDashboard() {
    	const id = this.state.deleteId;
    	this.props.dispatch(deleteDashboard(id));
    	this.setState({
			isDeleteModalOpen: !this.state.isDeleteModalOpen,
			deleteId: ""
    	});
	}

    onSortChange(field: string, direction: string) {
        this.props.dispatch(setFetchParams({sort: field, order: direction, page: 1}));
    }

    changePage(page: number) {
        this.props.dispatch(setFetchParams({sort: this.props.sort, order: this.props.order, page}));
    }

    onSearch(event: React.FormEvent<HTMLInputElement>) {
        //@ts-ignore
        const searchTerm = event.target.value;

        this.setState({ searchTerm: searchTerm }, () => {
            this.sendSearchParams();
        });
    }

    _sendSearchParams() {
        //@ts-ignore
        this.props.dispatch(setFetchParams({sort: this.props.sort, order: this.props.order, page: 1, searchTerm: this.state.searchTerm}));
    }

    renderBody(dashboards: any) {
        return (
            <tbody>
                {
                    dashboards && dashboards.map((item: any, index: number) => (
                        <tr key={index}>
                            <td width="330px"><a href={this.getDashboardUrl(item.id)} target="_blank" rel="noreferrer" className="title-link">{item.description}</a></td>
                            <td>{item.id}</td>
                            <td>by {item.updatedBy}</td>
                            <td className="align-middle">Updated {item.timestamp ? formatDate(item.timestamp) : ""}</td>
                            <td className="buttons">
                                <Dropdown
                                    buttonText={threeDots}
                                    className="dashboards-dropdown"
                                >
                                    <a href={this.getDashboardUrl(item.id)} target="_blank" rel="noreferrer" className="dropdown-item">View</a>
                                    {
                                        this.props.isSuperUser && (
                                            <Link className="dropdown-item" to={`admin/edit-dashboard/${item.id}`}>Edit</Link>
                                        )
                                    }

                                    <button className="dropdown-item" onClick={() => this.toggleCopyModal(item.id)}>Get link</button>

                                    {
                                        this.props.isSuperUser && (
                                            <Fragment>
                                                <div className="dropdown-divider"></div>
                                                <button className="dropdown-item delete" onClick={() => this.toggleDeleteModal(item.id)}>Delete</button>
                                            </Fragment>
                                        )
                                    }

                                </Dropdown>
                            </td>
                        </tr>
                    ))
                }
            </tbody>
        );
    }
    
    render() {
        return (
            <div className="dashboards__table">
                <div className="table-header">
                    <h4>Dashboards by users</h4>
                    <div className="search">
                        <input type="search" placeholder="Search dashboards" className="form-control" value={this.state.searchTerm} onChange={this.onSearch} />
                    </div>
                </div>
                <FetchPaginatedData
                    action={{function: fetchDashboards, payload: {}}}
                    storeKey="dashboards"
                    alwaysReload={true}
                    render={(data, isLoading, pagination) => (
                        <>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <TableHeading 
                                            name="description"
                                            label="Title"
                                            sortedBy={this.props.sort}
                                            sortedDirection={this.props.order}
                                            onChange={this.onSortChange}
                                            isLoading={isLoading}
                                        />

                                        <TableHeading
                                            name="Id"
                                            label="Id"
                                            sortedBy={this.props.sort}
                                            sortedDirection={this.props.order}
                                            noSort={true}
                                        />

                                        <TableHeading
                                            name="updatedBy"
                                            label="Updated by"
                                            sortedBy={this.props.sort}
                                            sortedDirection={this.props.order}
                                            noSort={true}
                                        />

                                        <TableHeading
                                            name="timestamp"
                                            label="Updated date"
                                            sortedBy={this.props.sort}
                                            sortedDirection={this.props.order}
                                            onChange={this.onSortChange}
                                            isLoading={isLoading}
                                        />

                                        <TableHeading
                                            name=""
                                            label=""
                                            sortedBy={this.props.sort}
                                            sortedDirection={this.props.order}
                                            noSort={true}
                                        />
                                    </tr>
                                </thead>

                                {(!isLoading) ? this.props.troubleLoading ? <tbody>
                                    <tr className="table__loading-row">
                                        <td colSpan={999}>
                                            <span className="trouble-loading-message">
                                                <h5>We're having trouble loading your data</h5>
                                                <button className="btn btn-outline-primary" onClick={() => { window.location.reload() }}>
                                                    Please try again
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody> : this.renderBody(data) : (
                                    <tbody>
                                        <tr className="table__loading-row">
                                            <td colSpan={999}>
                                                <span className="loading-sign">
                                                    <span className="loading-sign__spinner" />
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>

                            {!isLoading && pagination?.totalPages > 1 && <Pagination page={pagination.currentPage} total={pagination?.totalPages} setPage={this.changePage} />}
                        </>
                    )} />
                
                <Modal isOpen={this.state.isCopyModalOpen} className="copy-url-modal">
                    <ModalHeader>
                        <h5 className="modal-title">Copy dashboard link</h5>
                        <button type="button" className="btn btn-link" aria-label="Close" onClick={() => this.toggleCopyModal("")}>
                            <span aria-hidden="true">{close}</span>
                        </button>
                    </ModalHeader>
                    <ModalBody>
                        <div className="form-group mt-2">
                            <input className="form-control" type="text" readOnly value={this.state.copyModalUrl}/>
                            <CopyToClipboard text={this.state.copyModalUrl}>
                                <button className="btn btn-outline-primary" onClick={this.handleCopy}>Copy</button>
                            </CopyToClipboard>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-outline-danger" onClick={() => this.toggleCopyModal("")}>Close</button>
                    </ModalFooter>
                </Modal>
                
                <Modal isOpen={this.state.isDeleteModalOpen} className="confirm">
                    <ModalHeader>
                        <p className="modal-title">Are you sure you want to delete this dashboard?</p>
                    </ModalHeader>
                    <ModalFooter>
                        <button type="button" className="btn btn-outline-danger" onClick={() => this.toggleDeleteModal("")}>Close</button>
                        <button type="button" className="btn btn-danger" onClick={this.deleteDashboard}>Delete</button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => {
	return {
        troubleLoading : state.dashboards.troubleLoading,
        sort: state.dashboards.sort,
        order: state.dashboards.order,
        page: state.dashboards.page,
        searchTerm: state.dashboards.searchTerm
	}
};

export default connect(mapStateToProps)(Table);
